import { Modal, Row, Col, Typography, Button, Collapse, Flex, Tag } from "antd";
import PropTypes from "prop-types";
import "./GiftCardModal.scss";
import cart from "../../../assets/icons/cart.svg";
import { useTranslation } from "react-i18next";
import { memo, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, fetchCart } from "../../../features/cart/cartSlice";
import { hideLoader, showLoader } from "../../../features/loader/loaderSlice";
import { showToast } from "../../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import i18next from "i18next";
import { useNavigate } from "react-router-dom";

const { Title, Text, Paragraph } = Typography;

const GiftCardModal = ({ visible, onClose, product }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLogin = useSelector((state) => state.login.isLogin);
  const { items } = useSelector((state) => state.cart);
  console.log("items: ", items);

  const [quantity, setQuantity] = useState(1);
  const [selectedAmount, setSelectedAmount] = useState(null);

  // Memoized handlers
  const handleIncrease = useCallback(() => setQuantity((prev) => prev + 1), []);
  const handleDecrease = useCallback(
    () => setQuantity((prev) => (prev > 1 ? prev - 1 : prev)),
    []
  );

  const handleAmountSelect = useCallback((item) => {
    setSelectedAmount(item);
  }, []);

  // Memoized product data
  const productData = useMemo(() => product || {}, [product]);

  // Memoized text alignment based on language
  const textAlignment = useMemo(
    () => ({
      textAlign: i18next.language === "ar" ? "right" : "left",
    }),
    []
  );

  // handle add to cart
  const handleAddToCart = useCallback(() => {
    if (!isLogin) {
      showToast(t("cart.please_login"), "warning");
      navigate("/login");
      return;
    }

    if (productData?.is_already_in_cart) {
      showToast(t("cart.already_in_cart"), "info");
      onClose();
      navigate("/my-cart");

      return;
    }

    if (!selectedAmount) {
      showToast(t("cart.select_price"), "warning");
      return;
    }

    const payload = {
      productId: productData.id,
      amount: selectedAmount.sar,
      quantity,
    };

    dispatch(showLoader());
    dispatch(addToCart(payload))
      .unwrap()
      .then((res) => {
        console.log("res: ", res);
        showToast(res?.message || "Added to cart successfully", "success");
        dispatch(fetchCart(i18next.language));
        navigate("/my-cart");
      })
      .catch((err) => {
        console.error("❌ Error adding to cart:", err);
        showToast(err?.error?.message, "error");
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  }, [
    selectedAmount,
    productData.id,
    quantity,
    dispatch,
    navigate,
    t,
    isLogin,
  ]);

  // Memoized gallery images with proper keys
  const galleryImages = useMemo(
    () =>
      productData.image_gallery?.slice(0, 4).map((img, index) => (
        <img
          key={`gallery-${index}`}
          src={img}
          alt={`gallery-${index}`}
          style={{
            width: "60px",
            height: "60px",
            borderRadius: "8px",
            objectFit: "cover",
            border: "1px solid #ddd",
            cursor: "pointer",
          }}
        />
      )) || [],
    [productData.image_gallery]
  );

  // After (unique keys with fallbacks)
  const categoryTags = useMemo(() => {
    if (!productData.categories?.length) return [];

    return productData.categories.map((cat, index) => {
      const uniqueKey = `category-${cat.id || cat.name || index}-${index}`;

      return (
        <Tag key={uniqueKey} color="green">
          {cat.name}
        </Tag>
      );
    });
  }, [productData.categories]);

  // Memoized price buttons with proper keys
  const priceButtons = useMemo(
    () =>
      productData.prices?.map((item, idx) => (
        <Button
          key={`price-${idx}`}
          type={selectedAmount?.sar === item?.sar ? "primary" : "default"}
          className="amount-btn"
          onClick={() => handleAmountSelect(item)}
        >
          {item.coins}
        </Button>
      )) || [],
    [productData.prices, selectedAmount?.sar, handleAmountSelect]
  );

  // Memoized collapse items (fixed for new Antd Collapse API)
  const collapseItems = useMemo(
    () => [
      {
        key: "1",
        label: t("store_header.how_to_use"),
        children: (
          <div
            dangerouslySetInnerHTML={{
              __html:
                productData.redemption_instructions ||
                "Instructions will be provided.",
            }}
            className="collapes-text"
          />
        ),
      },
      {
        key: "2",
        label: t("store_header.term_and_condition"),
        children: (
          <Paragraph className="collapes-text">
            {productData.description || "Standard gift card terms apply."}
          </Paragraph>
        ),
      },
    ],
    [t, productData.redemption_instructions, productData.description]
  );

  if (!product) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={900}
      className="gift-modal"
    >
      <Row gutter={30}>
        {/* Left side - image & gallery */}
        <Col xs={24} md={12}>
          <div className="gift-modal-image">
            <img
              src={productData.product_image}
              alt={productData.name}
              style={{
                width: "100%",
                borderRadius: "10px",
                objectFit: "cover",
              }}
            />
          </div>

          {/* Gallery images */}
          {productData.image_gallery?.length > 0 && (
            <Flex
              wrap="wrap"
              justify="start"
              gap="small"
              style={{ marginTop: "10px" }}
            >
              {galleryImages}
            </Flex>
          )}
        </Col>

        {/* Right side - details */}
        <Col xs={24} md={12}>
          <Title level={4} className="gift-title">
            {productData.name}
          </Title>

          <Text type="secondary" className="tagline">
            {productData.tagline}
          </Text>

          {/* 💰 Prices */}
          {productData.prices?.length > 0 && (
            <>
              <Title
                style={{
                  color: "#fff",
                  ...textAlignment,
                }}
                level={5}
              >
                {t("product.select_amount")}
              </Title>
              <div className="denominations">{priceButtons}</div>
            </>
          )}

          {/* Category tags */}
          <div className="gift-category" style={{ margin: "15px 0" }}>
            {categoryTags}
          </div>

          {/* Quantity */}
          <div className="qty-box">
            <Text className="quantity-label">{t("store_header.quantity")}</Text>

            <div className="qty-container">
              <button
                key="decrease-btn"
                className="qty-btn"
                onClick={handleDecrease}
                type="button"
              >
                –
              </button>
              <span key="quantity-value" className="qty-value">
                {quantity}
              </span>
              <button
                key="increase-btn"
                className="qty-btn"
                onClick={handleIncrease}
                type="button"
              >
                +
              </button>
            </div>
          </div>

          {/* Add to cart button */}
          <fieldset className="card-button" onClick={handleAddToCart}>
            <legend>{t("store_header.coins")}</legend>
            <Flex
              key="cart-flex"
              justify="space-between"
              align="center"
              style={{ padding: "10px", width: "100%" }}
            >
              <div className="buy">
                {t("store_header.add_to_cart") || "Add to Cart"}
              </div>
              <img
                key="cart-icon"
                src={cart}
                alt="cart-img"
                className="cart-icon"
                style={{ cursor: "pointer" }}
              />
            </Flex>
          </fieldset>

          {/* Collapse for info - using new items API */}
          <Collapse
            ghost
            className="gift-collapse"
            style={{ marginTop: 20 }}
            items={collapseItems}
          />
        </Col>
      </Row>
    </Modal>
  );
};

GiftCardModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  product: PropTypes.object,
};

export default memo(GiftCardModal);
